import { ReactComponent as ThumbsUpIcon } from '@elementary/common-ui/assets/16x16/thumbs-up.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  StatusChip,
  WidgetButton,
} from '@elementary/common-ui/components';
import { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { Period } from '@elementary/common-ui/types';
import { RESULT_FEEDBACK_BUTTON_LABEL } from '@elementary/common-ui/components/TestCharts/constants';
import { styled, Tooltip, Typography } from '@mui/material';
import { AlertDestinationIcon } from 'shared/components/AlertDestinations';
import { IntegrationType } from 'shared/types';
import {
  EmailChannel,
  ExecutionAlertDestination,
  ModelFailureExecution,
  TestOverviewExecution,
} from 'shared/types/execution';
import { fontSize, space } from '@elementary/common-ui/style/utils';

const COLUMN_GAP = '80px';

const FirstChannelTypography = styled(Typography)`
  min-width: 0;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AdditionalChannelsTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.content.secondary};
  cursor: pointer;
  white-space: nowrap;
`;

type BarDetailProps = {
  title: string;
  value: JSX.Element | string;
};

const BarDetail = ({ title, value }: BarDetailProps) => (
  <FlexColumn sx={{ gap: space('smMinus') }}>
    <Typography sx={{ color: 'content.secondary', fontSize: fontSize('sm') }}>
      {title}
    </Typography>
    <Flex>{value}</Flex>
  </FlexColumn>
);

type Props = {
  data: ModelFailureExecution | TestOverviewExecution;
  trainingPeriod?: Period | null;
  detectionPeriod?: Period | null;
  onFeedbackClick?: () => void;
};

const getChannels = (alertDestination: ExecutionAlertDestination) => {
  if (alertDestination.type.toLowerCase() === 'email') {
    const emailChannel = JSON.parse(
      alertDestination.channel as string,
    ) as EmailChannel;

    return emailChannel.to ?? [];
  }

  return [alertDestination.channel as string];
};

const ExecutionOverview = ({
  data,
  trainingPeriod,
  detectionPeriod,
  onFeedbackClick,
}: Props) => {
  const channels = data.alertDestination
    ? getChannels(data.alertDestination)
    : [];
  const firstChannel = channels[0] ?? '-';
  const additionalChannelsCount = Math.max(channels.length - 1, 0);
  const allChannelsTooltip = channels.join(', ');

  return (
    <FlexSpace sx={{ gap: 5 }}>
      <Flex
        sx={{
          gap: (theme) => `${theme.tokens.space.xxl} ${COLUMN_GAP}`,
          flexWrap: 'wrap',
        }}
      >
        <BarDetail
          title="Selected Result"
          value={<StatusChip status={data?.status as ChipStatus} />}
        />

        <BarDetail
          title="Execution Timestamp"
          value={
            <Typography noWrap>
              {localizeDate(data?.executionTimestamp).format('MMM D LT')}
            </Typography>
          }
        />

        {data && 'lastRowCount' in data && data?.lastRowCount != null && (
          <BarDetail
            title="Last Row Count"
            value={
              <Typography noWrap>
                {data.lastRowCount.toLocaleString()}
              </Typography>
            }
          />
        )}
        {trainingPeriod != null && (
          <BarDetail
            title="Training Period"
            value={
              <Typography noWrap>
                {pluralizeString(
                  trainingPeriod.count,
                  trainingPeriod.period,
                  `${trainingPeriod.period}s`,
                )}
              </Typography>
            }
          />
        )}
        {detectionPeriod != null && (
          <BarDetail
            title="Detection Period"
            value={
              <Typography noWrap>
                {pluralizeString(
                  detectionPeriod.count,
                  detectionPeriod.period,
                  `${detectionPeriod.period}s`,
                )}
              </Typography>
            }
          />
        )}
        {data?.alertDestination != null && (
          <BarDetail
            title="Alert Destination"
            value={
              <FlexCenter sx={{ gap: 1, overflow: 'hidden' }}>
                <AlertDestinationIcon
                  type={data.alertDestination.type as IntegrationType}
                />
                <Tooltip title={allChannelsTooltip}>
                  <Flex sx={{ gap: 1 }}>
                    <FirstChannelTypography noWrap>
                      {firstChannel}
                    </FirstChannelTypography>
                    {additionalChannelsCount > 0 && (
                      <AdditionalChannelsTypography noWrap>
                        +{additionalChannelsCount}
                      </AdditionalChannelsTypography>
                    )}
                  </Flex>
                </Tooltip>
              </FlexCenter>
            }
          />
        )}
      </Flex>

      {!!onFeedbackClick && (
        <WidgetButton
          sx={{ margin: space('auto', 0, 0, 0), minWidth: 'fit-content' }}
          onClick={onFeedbackClick}
          startIcon={<ThumbsUpIcon />}
        >
          {RESULT_FEEDBACK_BUTTON_LABEL}
        </WidgetButton>
      )}
    </FlexSpace>
  );
};

export default ExecutionOverview;
